import { useState } from 'react'
import { cn } from '@/lib/utils'
import { ScrollReveal, TextReveal, useHoverCapable } from '@/components/motion'
import { TeamDiagram } from '@/components/landing/service'
import { Highlight } from '@/components/landing/about/Highlight'
import type { SolutionData } from '@/data/solutions'

const EASE = 'ease-[cubic-bezier(0.22,1,0.36,1)]'
const pad = (n: number) => String(n + 1).padStart(2, '0')

/**
 * The steps as large hover rows (after the home page's DirectionalList): a
 * big faded step number, the title and the description between hairlines.
 * On hover (fine pointers) the number takes the accent, the title shifts
 * and a small TeamDiagram slides in from the right edge of the row.
 * Touch and reduced motion get the static rows; the diagram is decorative.
 */
export function StepRows({ solution }: { solution: SolutionData }) {
  const hoverable = useHoverCapable()
  const [hovered, setHovered] = useState(-1)
  const Icon = solution.icon
  const titleId = `${solution.slug}-steps-title`

  return (
    <section id="how-it-works" aria-labelledby={titleId} className="landing-section scroll-mt-24">
      <div className="landing-container">
        <TextReveal as="h2" className="landing-text-display-xl max-w-[22ch] text-[var(--color-landing-gray-900)] dark:text-white">
          <span id={titleId}>
            How Templatean works for <Highlight color="blue">{solution.teamName.toLowerCase()} teams.</Highlight>
          </span>
        </TextReveal>

        <ol className="mt-14 border-t border-[var(--color-landing-gray-900)] dark:border-white/70 md:mt-20" onPointerLeave={() => setHovered(-1)}>
          {solution.steps.map((step, i) => {
            const on = hoverable && hovered === i
            return (
              <li
                key={step.title}
                onPointerEnter={() => hoverable && setHovered(i)}
                className="relative overflow-hidden border-b border-[var(--color-landing-gray-200)] dark:border-[var(--color-landing-gray-800)]"
              >
                <ScrollReveal
                  direction="none"
                  delay={i * 0.06}
                  className="grid items-center gap-x-10 gap-y-4 py-10 md:grid-cols-[minmax(0,9rem)_minmax(0,1fr)_minmax(0,0.9fr)] md:py-12 lg:grid-cols-[minmax(0,11rem)_minmax(0,1fr)_minmax(0,0.8fr)_12rem] lg:py-14"
                >
                  <span
                    aria-hidden
                    className={cn(
                      'font-[family-name:var(--font-landing-subdisplay)] text-[clamp(4rem,8vw,8.5rem)] font-light leading-[0.9] tracking-[-0.05em] transition-colors duration-300',
                      on ? 'text-[var(--color-landing-accent)] dark:text-[#4d44ff]' : 'text-black/10 dark:text-white/15'
                    )}
                  >
                    {pad(i)}
                  </span>
                  <h3
                    className={cn(
                      'landing-text-display-lg text-[var(--color-landing-gray-900)] transition-transform duration-500 dark:text-white',
                      EASE,
                      on && 'translate-x-3'
                    )}
                  >
                    {step.title}
                  </h3>
                  <p className="landing-text-body-lg max-w-[44ch] text-pretty text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">
                    {step.description}
                  </p>
                  {/* Reserved column so the copy never runs under the diagram */}
                  <span aria-hidden className="hidden lg:block" />
                </ScrollReveal>

                <div
                  aria-hidden
                  className={cn(
                    'pointer-events-none absolute right-0 top-1/2 hidden h-44 w-44 -translate-y-1/2 transition-[clip-path,translate,opacity] duration-500 lg:block',
                    EASE,
                    on ? 'opacity-100 [clip-path:inset(0_0_0_0)]' : 'translate-x-8 opacity-0 [clip-path:inset(0_0_0_100%)]'
                  )}
                >
                  {hoverable && (
                    <TeamDiagram teamName={solution.teamName} icon={Icon} nodes={step.nodes} layout={i} active={on} />
                  )}
                </div>
              </li>
            )
          })}
        </ol>
      </div>
    </section>
  )
}

export default StepRows
